import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Category } from '../../services/categoryService';
import { Tour } from '../../types';
import { useTheme } from '../../contexts/ThemeContext';

interface SpinResultProps {
    item: Category | Tour;
    mode: 'categories' | 'tours';
    categoryImages?: Record<string, string>;
    onSpinAgain: () => void;
    onClose: () => void;
}

const SpinResult: React.FC<SpinResultProps> = ({ item, mode, categoryImages, onSpinAgain, onClose }) => {
    const { actualTheme } = useTheme();

    const isTour = 'title' in item;
    const name = isTour ? item.title : item.name;
    const description = isTour ? item.shortDescription || item.description : item.description;

    // Resolve Image: Direct for Tour, Lookup for Category
    const image = isTour
        ? item.images?.[0]
        : (categoryImages && categoryImages[item.id] ? categoryImages[item.id] : undefined);

    // FIX: Categories use singular route /category/:id
    const link = isTour ? `/tours/${item.id}` : `/category/${item.id}`;

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={`relative rounded-[2.5rem] overflow-hidden max-w-sm mx-auto shadow-2xl border border-white/20 backdrop-blur-2xl ${actualTheme === 'dark' 
                ? 'bg-slate-900/60' 
                : 'bg-white/80'
            }`}
        >
            {/* Image Section - Full width top */}
            <div className="relative h-72 w-full overflow-hidden">
                {image ? (
                    <img
                        src={image}
                        alt={name}
                        className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                    />
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary-600 to-blue-700 flex items-center justify-center">
                        <span className="text-7xl animate-bounce">✨</span>
                    </div>
                )}
                {/* Badge Overlay */}
                <div className="absolute top-6 right-6 bg-white/20 backdrop-blur-md border border-white/30 text-white px-4 py-1.5 rounded-full text-[10px] font-black shadow-xl uppercase tracking-[0.2em]">
                    {mode === 'tours' ? 'Expedition' : 'Signature Category'}
                </div>
                
                {/* Gradient Shadow bottom of image */}
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/20 to-transparent" />
            </div>

            {/* Content Section */}
            <div className="p-8 text-center">
                <motion.h3 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className={`text-2xl font-black uppercase tracking-widest mb-3 font-serif ${actualTheme === 'dark' ? 'text-white' : 'text-slate-900'
                    }`}
                >
                    {name}
                </motion.h3>

                <motion.p 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className={`text-sm mb-8 line-clamp-2 font-medium leading-relaxed ${actualTheme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                    }`}
                >
                    {description}
                </motion.p>

                <div className="flex flex-row gap-4">
                    {/* Secondary Action - Explore */}
                    <Link
                        to={link}
                        onClick={onClose}
                        className={`flex-1 py-4 px-6 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] text-center border-2 transition-all flex items-center justify-center ${actualTheme === 'dark'
                            ? 'border-white/10 text-slate-100 hover:bg-white/10 hover:border-white/20'
                            : 'border-slate-200 text-slate-800 hover:bg-slate-50 hover:border-slate-300'
                            }`}
                    >
                        Explore
                    </Link>

                    {/* Primary Action - Spin Again - System Theme */}
                    <button
                        onClick={onSpinAgain}
                        className="flex-1 group relative overflow-hidden bg-primary-500 text-white font-black py-4 px-6 rounded-2xl text-[10px] uppercase tracking-[0.2em] transition-all hover:scale-105 active:scale-95 shadow-xl shadow-primary-500/20"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-primary-400 to-transparent opacity-0 group-hover:opacity-20 transition-opacity" />
                        <span className="relative z-10">SPIN AGAIN</span>
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

export default SpinResult;
